import Dagre from "@dagrejs/dagre";
import { Edge, Node } from "@xyflow/react";
import { v4 as uuid } from "uuid";
import {
  AppNode,
  BaseNodeData,
  DecisionResult,
  UnderstandingRepresentation,
} from "./types";

export const REACT_FLOW_NODE_WIDTH = 350;
export const REACT_FLOW_NODE_HEIGHT = 225;

export const convertToReactFlowNodesAndEdges = (
  {
    name,
    question,
    line,
    logicalConnective,
    logicalOperator,
    subConditions,
  }: UnderstandingRepresentation,
  parentNodeId?: string
): { nodes: AppNode[]; edges: Edge[] } => {
  const nodeId = uuid();

  const baseNode: Node<BaseNodeData> = {
    id: nodeId,
    data: {
      label: name,
      question,
      line,
      isNegated: logicalOperator === "NOT",
      value: DecisionResult.DontKnow, // TODO (CTRL-14): Use the type that comes back from the Acorn API instead
    },
    position: { x: 0, y: 0 },
    style: { width: REACT_FLOW_NODE_WIDTH, height: REACT_FLOW_NODE_HEIGHT },
  };

  const reactFlowNode: AppNode = logicalConnective
    ? {
        ...baseNode,
        data: { ...baseNode.data, logicalConnector: logicalConnective },
        type: "logicalConnector",
      }
    : { ...baseNode, type: "leafCondition" };

  return (subConditions ?? []).reduce<{
    nodes: AppNode[];
    edges: Edge[];
  }>(
    ({ nodes, edges }, subRepresentation) => {
      const subgraphNodesAndEdges = convertToReactFlowNodesAndEdges(
        subRepresentation,
        nodeId
      );

      return {
        nodes: [...nodes, ...subgraphNodesAndEdges.nodes],
        edges: [...edges, ...subgraphNodesAndEdges.edges],
      };
    },
    {
      nodes: [reactFlowNode],
      edges: parentNodeId
        ? [
            {
              id: `${parentNodeId}-${nodeId}`,
              source: parentNodeId,
              target: nodeId,
            },
          ]
        : [],
    }
  );
};

const buildUnderstandingRepresentationForNodeId = (
  nodeId: string,
  nodes: AppNode[],
  edges: Edge[]
): UnderstandingRepresentation | undefined => {
  const node = nodes.find(({ id }) => id === nodeId);
  if (!node) return;

  const { id, data, type } = node;

  const childNodes = edges.reduce<UnderstandingRepresentation[]>(
    (representations, { source, target }) => {
      if (source !== id) return representations;
      const childNodeRepresentation = buildUnderstandingRepresentationForNodeId(
        target,
        nodes,
        edges
      );
      return childNodeRepresentation
        ? [...representations, childNodeRepresentation]
        : representations;
    },
    []
  );

  const { label, question, line, isNegated } = data;

  return {
    name: label,
    question,
    line,
    ...(isNegated ? { logicalOperator: "NOT" } : {}),
    ...(type === "logicalConnector"
      ? { logicalConnective: data.logicalConnector }
      : {}),
    ...(childNodes.length ? { subConditions: childNodes } : {}),
  };
};

export const convertToUnderstandingRepresentation = (
  nodes: AppNode[],
  edges: Edge[]
) =>
  nodes.reduce<UnderstandingRepresentation[]>((representations, { id }) => {
    // filter out non-root nodes
    if (edges.some(({ target }) => target === id)) return representations;

    const rootRepresentation = buildUnderstandingRepresentationForNodeId(
      id,
      nodes,
      edges
    );

    return rootRepresentation
      ? [...representations, rootRepresentation]
      : representations;
  }, []);

export type LayoutDirection = "TB" | "LR";

export const layoutTree = (
  nodes: AppNode[],
  edges: Edge[],
  direction: LayoutDirection
): { nodes: AppNode[]; edges: Edge[] } => {
  const dagreGraph = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));
  nodes.forEach((node) =>
    dagreGraph.setNode(node.id, {
      ...node,
      width: REACT_FLOW_NODE_WIDTH,
      height: REACT_FLOW_NODE_HEIGHT,
    })
  );

  Dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const position = dagreGraph.node(node.id);
      /* The dagre node anchor point (center center) needs to be shifted to the top left
      so it matches the React Flow node anchor point (left top). */
      const x = position.x - REACT_FLOW_NODE_WIDTH / 2;
      const y = position.y - REACT_FLOW_NODE_HEIGHT / 2;

      return { ...node, position: { x, y } };
    }),
    edges,
  };
};

export const negateDecision = (decision: DecisionResult) =>
  decision === DecisionResult.Yes
    ? DecisionResult.No
    : decision === DecisionResult.No
      ? DecisionResult.Yes
      : DecisionResult.DontKnow;

export const andNodeDecision = (childDecisions: DecisionResult[]) =>
  childDecisions.some((decision) => decision === DecisionResult.No)
    ? DecisionResult.No
    : childDecisions.every((decision) => decision === DecisionResult.Yes)
      ? DecisionResult.Yes
      : DecisionResult.DontKnow;

export const orNodeDecision = (childDecisions: DecisionResult[]) =>
  childDecisions.some((decision) => decision === DecisionResult.Yes)
    ? DecisionResult.Yes
    : childDecisions.every((decision) => decision === DecisionResult.No)
      ? DecisionResult.No
      : DecisionResult.DontKnow;
